import { eachDayOfInterval, format } from 'date-fns';

import { ComparativeSection } from '../../utils/comparativeSectionUtil';
import { excludeHoliday } from '../../utils/excludeHoliday';
import { excludeWeekday } from '../../utils/excludeWeekday';
import { formatWeekdayAndHoliday } from '../../utils/formatWeekdayAndHoliday';
import { ChainReportsFormConditions } from '../chainReportsFormConditions';
import { ChainStoreReportsSettingDateRangeParams } from '../chainStoreReportsSettingDateRangeParams';
import { DataChainStoreKiParams } from '../dataChainStoreKi';
import {
  DataChainStoreShuParams,
  DataChainStoreShuSummaryParams,
} from '../dataChainStoreShu';
import { DataKadoGraphParams } from '../dataKadoGraph';
import { generateStartEndFromYmdLists } from '../generateStartEndFromYmdLists';

/**
 * チェーン店テーブル 3 API（ki / shu / shu/whole）に載せる平日/土日祝比較のクエリ値。
 * UI / Redux の `'平日/土日祝比較'` をそのまま送ると未知 enum で 400 になる。
 */
export const CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION =
  'weekdayHoliday' as const;

export type ChainStoreApiComparativeSection =
  typeof CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION;

/**
 * 旧お気に入りの `dateUnit === 'カスタム'` は範囲選択と同じ扱い（`changeNameDateUnit`）。
 */
export const isChainStoreRangeSelectDateUnit = (
  dateUnit: ChainStoreReportsSettingDateRangeParams['dateUnit']
): boolean => dateUnit === '範囲選択' || dateUnit === 'カスタム';

/**
 * UI の比較区分をチェーン店テーブル API 用の値へ変換する。
 * 範囲選択系かつ平日/土日祝比較のときだけ `'weekdayHoliday'`。それ以外はクエリに載せない。
 */
export const mapChainStoreApiComparativeSection = (
  comparativeSection: ComparativeSection | undefined,
  dateUnit: ChainStoreReportsSettingDateRangeParams['dateUnit']
): ChainStoreApiComparativeSection | undefined => {
  if (
    comparativeSection === '平日/土日祝比較' &&
    isChainStoreRangeSelectDateUnit(dateUnit)
  ) {
    return CHAIN_STORE_API_WEEKDAY_HOLIDAY_COMPARATIVE_SECTION;
  }
  return undefined;
};

const ymdFieldsFromWeekdayHolidayComparison = (
  ymdList: string[] | undefined,
  ymdComparisonList: string[] | null | undefined
):
  | {
      ymdList: string[] | undefined;
      ymdComparisonList: string[];
    }
  | {
      ymdList: string[] | undefined;
    } =>
  ymdComparisonList
    ? {
        ymdList,
        ymdComparisonList,
      }
    : {
        ymdList,
      };

/**
 * 平日・土日祝比較を行う場合、日付リストを平日（ymdList）と土日祝（ymdComparisonList）に分割する
 *
 * @param dateList - 分割対象の日付リスト
 * @param holidays - 祝日の yyyy-MM-dd 配列（必須）
 * @param isComparison - 比較を行う場合は true。false のときは ymdComparisonList を undefined にして返す
 * @returns ymdList（平日）, ymdComparisonList（土日祝、isComparison が true の場合のみ）
 */
export const splitDateListByWeekdayAndHoliday = (
  dateList: Date[],
  holidays: string[],
  isComparison: boolean
): {
  ymdList: string[] | undefined;
  ymdComparisonList: string[] | undefined;
} => {
  const weekdayHolidayResult = formatWeekdayAndHoliday({
    ymdList: excludeHoliday(dateList, holidays).map((v) =>
      format(v, 'yyyy-MM-dd')
    ),
    ymdComparisonList: excludeWeekday(dateList, holidays).map((v) =>
      format(v, 'yyyy-MM-dd')
    ),
  });
  return {
    ymdList: weekdayHolidayResult.ymdList,
    ymdComparisonList: isComparison
      ? weekdayHolidayResult.ymdComparisonList
      : undefined,
  };
};

/**
 * カスタム期間かつ平日/土日祝比較のとき、フォーム条件から API 用の ymdList（平日）と ymdComparisonList（土日祝）を算出する
 * ymdList と ymdComparisonList をマージしてから平日/土日祝に分離する。マージ結果が空の場合は startDate/endDate から区間を生成する
 *
 * @param formConditions フォームの検索条件（ymdList, ymdComparisonList または startDate, endDate を持つ）
 * @param holidays 祝日の yyyy-MM-dd 配列（必須）
 * @param isComparison 比較を行う場合は true
 * @returns ymdList（平日）, ymdComparisonList（土日祝）。日付が算出できない場合は両方 undefined
 */
export const buildWeekdayHolidayParamsFromCustomForm = (
  formConditions: ChainReportsFormConditions,
  holidays: string[],
  isComparison: boolean
): {
  ymdList: string[] | undefined;
  ymdComparisonList: string[] | undefined;
} => {
  const mergedYmdList = Array.from(
    new Set([
      ...(formConditions.ymdList ?? []),
      ...(formConditions.ymdComparisonList ?? []),
    ])
  );

  const dateList =
    mergedYmdList.length > 0
      ? [...mergedYmdList]
          .sort((a, b) => (a > b ? 1 : -1))
          .map((v) => new Date(v))
      : formConditions.startDate !== undefined &&
        formConditions.endDate !== undefined
      ? eachDayOfInterval({
          start: new Date(formConditions.startDate),
          end: new Date(formConditions.endDate),
        })
      : [];

  if (dateList.length === 0) {
    return {
      ymdList: undefined,
      ymdComparisonList: undefined,
    };
  }

  return splitDateListByWeekdayAndHoliday(dateList, holidays, isComparison);
};

/**
 * ymdList と ymdComparisonList をマージしてソートし、その先頭・末尾を startDate/endDate として返す。
 *
 * @param ymdList - yyyy-MM-dd 配列（例: 平日）
 * @param ymdComparisonList - yyyy-MM-dd 配列（例: 土日祝）
 * @param fallback - マージ結果が空のときに使う startDate/endDate
 * @returns startDate, endDate（マージ結果または fallback。いずれも undefined の可能性あり）
 */
export const getStartEndFromMergedYmdLists = (
  ymdList: string[] | undefined,
  ymdComparisonList: string[] | undefined,
  fallback?: { startDate?: string; endDate?: string }
): { startDate: string | undefined; endDate: string | undefined } => {
  const merged = [...(ymdList ?? []), ...(ymdComparisonList ?? [])].sort(
    (a, b) => a.localeCompare(b)
  );
  return {
    startDate: merged.at(0) ?? fallback?.startDate,
    endDate: merged.at(-1) ?? fallback?.endDate,
  };
};

/**
 * チェーン店レポートのテーブル API に載せる日付フィールドの最終形を確定する。
 *
 * - 自由選択は日付列挙（ymdList / ymdComparisonList）。平日/土日祝比較でも weekdayHoliday 化しない。
 * - 範囲選択（旧お気に入りの `カスタム` 含む）かつ平日/土日祝比較は startDate / endDate と
 *   `comparativeSection=weekdayHoliday`。ymd 系・比較日付は載せない。
 * - 日 / 週 / 月 × 平日/土日祝比較は短期間のため従来どおり列挙する。
 * - それ以外の期間単位では、startDate / endDate（必要なら比較区間も）で送る。
 */
export const finalizeChainStoreApiDateRequestFields = ({
  dateRangeParams,
  ymdList,
  ymdComparisonList = null,
  startDate,
  endDate,
  startComparisonDate,
  endComparisonDate,
  comparativeSection,
}: {
  dateRangeParams: ChainStoreReportsSettingDateRangeParams['dateUnit'];
  ymdList?: string[];
  ymdComparisonList?: string[] | null;
  startDate?: string;
  endDate?: string;
  startComparisonDate?: string;
  endComparisonDate?: string;
  comparativeSection?: ComparativeSection;
}):
  | {
      startDate: string | undefined;
      endDate: string | undefined;
    }
  | {
      ymdList: string[] | undefined;
      ymdComparisonList: string[];
    }
  | {
      startDate: string | undefined;
      endDate: string | undefined;
      startComparisonDate: string;
      endComparisonDate: string;
    }
  | {
      ymdList: string[] | undefined;
    }
  | {
      startDate: string | undefined;
      endDate: string | undefined;
      comparativeSection: ChainStoreApiComparativeSection;
    } => {
  // 自由選択は飛び日指定のため列挙を維持する（平日/土日祝比較でも weekdayHoliday にしない）
  if (dateRangeParams === '自由選択') {
    return ymdFieldsFromWeekdayHolidayComparison(ymdList, ymdComparisonList);
  }

  const apiComparativeSection = mapChainStoreApiComparativeSection(
    comparativeSection,
    dateRangeParams
  );
  if (apiComparativeSection !== undefined) {
    // ストア上の平日・土日祝列挙をマージした区間を送る。ymdList 先頭末尾だけだと末尾の土日祝が落ちる。
    const mergedDateRange = getStartEndFromMergedYmdLists(
      ymdList,
      ymdComparisonList ?? undefined,
      {
        startDate,
        endDate,
      }
    );
    return {
      startDate: mergedDateRange.startDate,
      endDate: mergedDateRange.endDate,
      comparativeSection: apiComparativeSection,
    };
  }

  // 日 / 週 / 月 × 平日/土日祝比較は現状どおり列挙（短期間。414 対象外）
  if (comparativeSection === '平日/土日祝比較') {
    return ymdFieldsFromWeekdayHolidayComparison(ymdList, ymdComparisonList);
  }

  // チェーン店レポートの日付検索はymdListで現状管理をされている
  // 表示項目やマーキングの切り替え時などに、ymdComparisonListが存在せず、start endのみがある場合があるためそれを考慮する
  if (ymdComparisonList || (startComparisonDate && endComparisonDate)) {
    return {
      startDate,
      endDate,
      startComparisonDate: startComparisonDate ?? '',
      endComparisonDate: endComparisonDate ?? '',
    };
  }

  return {
    startDate,
    endDate,
  };
};

export type ChainReportsFormConditionsDateFields = Pick<
  ChainReportsFormConditions,
  | 'ymdList'
  | 'ymdComparisonList'
  | 'startDate'
  | 'endDate'
  | 'startComparisonDate'
  | 'endComparisonDate'
>;

export type ChainStoreApiDateParams = ReturnType<
  typeof finalizeChainStoreApiDateRequestFields
>;

export type ChainStoreComparisonGraphEndDateFallback = {
  ymdList?: string[];
  ymdComparisonList?: string[];
  endDate?: string;
};

const isChainStoreApiDateParamsWithEndDate = (
  apiDateParams: ChainStoreApiDateParams
): apiDateParams is Extract<
  ChainStoreApiDateParams,
  { endDate: string | undefined }
> => 'endDate' in apiDateParams;

const isChainStoreApiDateParamsWithYmdList = (
  apiDateParams: ChainStoreApiDateParams
): apiDateParams is Extract<
  ChainStoreApiDateParams,
  { ymdList: string[] | undefined }
> => 'ymdList' in apiDateParams;

/**
 * チェーン店レポートのテーブル API に載せる日付フィールドを組み立てる。
 *
 * 1. 検索条件の ymd リストから、API 用の start/end 系フィールドを生成する。
 * 2. 期間単位（dateUnit）と比較区分 (comparativeSection) に応じて、ymdList で送るか start/end で送るかを確定する。
 *
 * `dateUnit` が未定のときは undefined（呼び出し側でテーブル向けリクエストを組み立てない想定）。
 * チェーン店レポートの selector などから利用する。
 */
export const buildChainStoreApiDateParams = (
  searchCondition: ChainReportsFormConditionsDateFields,
  dateUnit: ChainStoreReportsSettingDateRangeParams['dateUnit'] | undefined,
  comparativeSection: ComparativeSection | undefined
): ChainStoreApiDateParams | undefined => {
  if (dateUnit === undefined) {
    return undefined;
  }
  const dateFieldsForApi = generateStartEndFromYmdLists(searchCondition);
  return finalizeChainStoreApiDateRequestFields({
    dateRangeParams: dateUnit,
    comparativeSection,
    ymdList: dateFieldsForApi.ymdList,
    ymdComparisonList: dateFieldsForApi.ymdComparisonList ?? null,
    startDate: dateFieldsForApi.startDate,
    endDate: dateFieldsForApi.endDate,
    startComparisonDate: dateFieldsForApi.startComparisonDate,
    endComparisonDate: dateFieldsForApi.endComparisonDate,
  });
};

/**
 * ymdList と ymdComparisonList をマージし、重複除去・昇順ソートした配列を返す。
 *
 * @param ymdList - yyyy-MM-dd 配列（例: 平日）
 * @param ymdComparisonList - yyyy-MM-dd 配列（例: 土日祝）
 * @returns マージ・ソート・重複除去後の yyyy-MM-dd 配列
 */
export const mergeYmdLists = (
  ymdList: string[] | undefined,
  ymdComparisonList: string[] | undefined
): string[] => {
  const merged = [...(ymdList ?? []), ...(ymdComparisonList ?? [])];
  return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
};

/**
 * 推移グラフ（機種比較）の終了日ソースを、テーブル API と同じ期間から解決する。
 *
 * チェーン店レポートのテーブルは自由選択・日/週/月の平日/土日祝以外で startDate/endDate を送るため、
 * テーブル setting に ymdList が無いことがある。ymdList 末尾だけを見ると当日にフォールバックし、
 * 期間スライダー後にテーブルとグラフの期間がずれる。
 */
export const resolveChainStoreComparisonGraphEndDateSource = (
  apiDateParams: ChainStoreApiDateParams | undefined,
  fallback: ChainStoreComparisonGraphEndDateFallback
): string | undefined => {
  if (apiDateParams !== undefined) {
    if (
      isChainStoreApiDateParamsWithEndDate(apiDateParams) &&
      apiDateParams.endDate !== undefined
    ) {
      return apiDateParams.endDate;
    }
    if (isChainStoreApiDateParamsWithYmdList(apiDateParams)) {
      const ymdComparisonList =
        'ymdComparisonList' in apiDateParams
          ? apiDateParams.ymdComparisonList
          : undefined;
      const endDateFromApi = getStartEndFromMergedYmdLists(
        apiDateParams.ymdList,
        ymdComparisonList,
        fallback
      ).endDate;
      if (endDateFromApi !== undefined) {
        return endDateFromApi;
      }
    }
  }

  return getStartEndFromMergedYmdLists(
    fallback.ymdList,
    fallback.ymdComparisonList,
    fallback
  ).endDate;
};

/**
 * チェーン店レポートの Summary API 向けに、日付フィールドを組み立てる。
 *
 * 検索条件は generateStartEndFromYmdLists で API 用に揃えたうえで分岐する。
 * - 比較区分が「平日/土日祝比較」のとき: getStartEndFromMergedYmdLists で ymdList / ymdComparisonList をマージした区間の startDate / endDate のみを返す。
 * - それ以外: finalizeChainStoreApiDateRequestFields で、Summary 向けに送る形を確定する。
 *
 * dateUnit が undefined のときは undefined（テーブル用ビルダーと同様）。
 */
export const buildChainStoreApiDateParamsForSummary = (
  searchCondition: ChainReportsFormConditionsDateFields,
  dateUnit: ChainStoreReportsSettingDateRangeParams['dateUnit'] | undefined,
  comparativeSection: ComparativeSection | undefined
): ChainStoreApiDateParams | undefined => {
  if (dateUnit === undefined) {
    return undefined;
  }
  const dateFieldsForApi = generateStartEndFromYmdLists(searchCondition);
  if (comparativeSection === '平日/土日祝比較') {
    return getStartEndFromMergedYmdLists(
      dateFieldsForApi.ymdList,
      dateFieldsForApi.ymdComparisonList,
      {
        startDate: dateFieldsForApi.startDate,
        endDate: dateFieldsForApi.endDate,
      }
    );
  }
  return finalizeChainStoreApiDateRequestFields({
    dateRangeParams: dateUnit,
    ymdList: dateFieldsForApi.ymdList,
    startDate: dateFieldsForApi.startDate,
    endDate: dateFieldsForApi.endDate,
    comparativeSection,
  });
};

type ChainStoreDateExcludableSettings = (
  | ChainReportsFormConditions
  | DataChainStoreKiParams
  | DataChainStoreShuParams
  | DataKadoGraphParams
) & {
  comparativeSection?: DataChainStoreKiParams['comparativeSection'];
};

/**
 * settingsから日付周りのパラメータを除外する関数
 * 検索実行時に使用されるのはpayload側の情報であり、
 * settings内のstartDate, endDate, startComparisonDate, endComparisonDate,
 * ymdList, ymdComparisonList, comparativeSectionは必要ないため、これらをsettingsから除外する
 * 同じ処理を複数箇所で行う必要があるため、その処理を共通化している
 *
 * comparativeSection も剥がすのは、BE が data.setting に weekdayHoliday をエコーし
 * ymd 列挙と同居すると 400 になるため。日付セットは常にセレクタ由来だけを載せる。
 * @param settings - 検索条件
 */
export const getExcludeDates = (settings: ChainStoreDateExcludableSettings) => {
  // 比較期間を持つの場合の処理
  const {
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
    ymdList,
    ymdComparisonList,
    comparativeSection,
    ...restParams
  } = settings;
  return restParams;
};

/**
 * settingsから日付周りのパラメータを除外する関数
 * 検索実行時に使用されるのはpayload側の情報であり、
 * settings内の startDate, endDate, ymdList, comparativeSectionは必要ないため、これらをsettingsから除外する
 * 同じ処理を複数箇所で行う必要があるため、その処理を共通化している
 *
 * Summary は comparativeSection 未公開のため、エコーされても載せない。
 * @param settings - 検索条件
 */
export const getExcludeDatesWithoutComparision = (
  settings: DataChainStoreShuSummaryParams & {
    comparativeSection?: DataChainStoreKiParams['comparativeSection'];
  }
) => {
  const { startDate, endDate, ymdList, comparativeSection, ...restParams } =
    settings;
  return restParams;
};
